import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useWorkflowStore } from '../store/workflowStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 8000;
// Ventana para recuperar resultados que el nodo nunca recibió (6 h).
const RECONCILE_WINDOW_MS = 6 * 60 * 60 * 1000;

/**
 * useNodeInFlightSync — E6c.5
 *
 * Reconstruye, desde el servidor, qué nodos tienen generaciones en curso.
 * Gracias a esto el badge y el estado "generando" sobreviven a una recarga de
 * página, y cuando una generación vieja termina (la que ya no es la activa del
 * nodo) se avisa que quedó en la galería — sin pisar nunca el nodo.
 *
 * Se monta UNA vez en el Studio.
 */
export const useNodeInFlightSync = () => {
  const projectId = useWorkflowStore((s) => s.currentProjectId);
  const timerRef = useRef(null);
  const knownRef = useRef(new Map());   // execution_id -> node_id
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    knownRef.current = new Map();
    const store = () => useWorkflowStore.getState();

    if (!projectId) {
      store().replaceInFlight({});
      return undefined;
    }

    const fetchActive = async () => {
      try {
        const res = await axios.get(`${API}/executions/active/list`, {
          params: { project_id: projectId },
        });
        if (!aliveRef.current) return 0;
        const rows = res.data?.active || [];
        const map = {};
        const seen = new Map();
        for (const r of rows) {
          if (!r.node_id) continue;
          seen.set(r.id, r.node_id);
          (map[r.node_id] = map[r.node_id] || []).push({
            execution_id: r.id,
            status: r.status,
            result_type: r.result_type,
            created_at: r.created_at,
            started_at: r.started_at,
          });
        }
        // Lo que estaba y ya no está: terminó. Hay que cerrarlo acá porque el
        // nodo pudo haber perdido su propio polling (recarga de página,
        // remonte del canvas por autosave/LMX, cambio de pestaña).
        for (const [execId, nodeId] of knownRef.current.entries()) {
          if (seen.has(execId)) continue;
          // eslint-disable-next-line no-await-in-loop
          await settleFinished(nodeId, execId);
        }
        knownRef.current = seen;
        store().replaceInFlight(map);
        return rows.length;
      } catch (_e) {
        return 0;
      }
    };

    /**
     * Cierra una generación que salió de la lista de activas.
     *  · Si era LA del nodo (la última mandada) y terminó bien, su resultado
     *    se escribe en el nodo: es el que corresponde a su prompt actual.
     *  · Si era una anterior, no pisa el nodo: sólo avisa que quedó en la
     *    galería (y la usa si el nodo está vacío y sin nada en curso).
     */
    const settleFinished = async (nodeId, execId) => {
      const s0 = store();
      const isActive = s0.isActiveExecution(nodeId, execId);
      let exec = null;
      try {
        const res = await axios.get(`${API}/executions/${execId}`);
        exec = res.data?.execution;
      } catch (_e) { return; }
      if (!exec) return;

      if (exec.status !== 'completed') {
        // cancelled/failed: el nodo ya lo refleja por su propio camino.
        return;
      }

      const primary = (exec.results || [])[0] || {};
      const assets = exec.assets || [];
      const outputAsset = assets.find((a) => a.role === 'output') || assets[0] || null;
      const url = primary.output_url || outputAsset?.url || null;
      const assetId = primary.output_asset_id || outputAsset?.id || null;

      const s = store();
      const node = s.nodes.find((n) => n.id === nodeId);
      if (!node || !url) {
        if (!isActive) s.bumpGalleryNotice(nodeId);
        return;
      }

      if (isActive) {
        if (node.data?.outputUrl === url) return;   // ya lo tiene
        s.updateNodeData(nodeId, {
          hasOutput: true,
          outputUrl: url,
          assetId,
          processedAt: exec.finished_at || new Date().toISOString(),
        });
        return;
      }

      // No es la última mandada: va a la galería.
      s.bumpGalleryNotice(nodeId);
      const hasActive = (s.inFlight[nodeId] || []).length > 0;
      if (!node.data?.hasOutput && !hasActive) {
        s.updateNodeData(nodeId, {
          hasOutput: true,
          outputUrl: url,
          assetId,
          processedAt: exec.finished_at || new Date().toISOString(),
        });
      }
    };

    const loop = async () => {
      const count = await fetchActive();
      if (!aliveRef.current) return;
      // Sin nada en curso se espera más: el badge se despierta con el evento.
      timerRef.current = setTimeout(loop, count > 0 ? POLL_MS : POLL_MS * 3);
    };
    loop();

    /**
     * Reconciliación al ABRIR el proyecto: si una generación terminó mientras
     * el usuario no estaba (pestaña cerrada, recarga, remonte del canvas), su
     * nodo quedó vacío aunque el resultado esté guardado. Se rellena una sola
     * vez, sólo en nodos vacíos y sin nada en curso.
     */
    const reconcileOnOpen = async (attempt = 0) => {
      if (!aliveRef.current) return;
      const s = store();
      if (!s.nodes.length) {
        if (attempt < 4) setTimeout(() => reconcileOnOpen(attempt + 1), 1500);
        return;
      }
      try {
        const res = await axios.get(`${API}/executions/project/${projectId}`,
          { params: { limit: 50 } });
        if (!aliveRef.current) return;
        const since = Date.now() - RECONCILE_WINDOW_MS;
        const pending = [];
        const seenNodes = new Set();
        for (const e of res.data?.executions || []) {
          if (e.status !== 'completed' || !e.node_id) continue;
          if (seenNodes.has(e.node_id)) continue;      // sólo la más reciente
          if (new Date(e.created_at).getTime() < since) continue;
          const st = store();
          const node = st.nodes.find((n) => n.id === e.node_id);
          if (!node || node.data?.hasOutput) continue;
          if ((st.inFlight[e.node_id] || []).length) continue;
          seenNodes.add(e.node_id);
          pending.push(e);
          if (pending.length >= 5) break;
        }
        for (const e of pending) {
          store().setNodeActiveExecution(e.node_id, e.id);
          // eslint-disable-next-line no-await-in-loop
          await settleFinished(e.node_id, e.id);
        }
        if (pending.length) {
          console.info(`[inflight] ${pending.length} resultado(s) recuperado(s) `
            + 'de generaciones que terminaron sin el canvas abierto');
        }
      } catch (_e) { /* no es crítico */ }
    };
    setTimeout(() => reconcileOnOpen(), 1200);

    const onChanged = () => { fetchActive(); };
    const onFocus = () => { fetchActive(); };
    window.addEventListener('executions:changed', onChanged);
    window.addEventListener('focus', onFocus);

    return () => {
      aliveRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      window.removeEventListener('executions:changed', onChanged);
      window.removeEventListener('focus', onFocus);
    };
  }, [projectId]);
};

export default useNodeInFlightSync;
